import React, { useState } from 'react'
import { LinkIcon, Loader2Icon, SearchIcon, XIcon } from 'lucide-react'
import {
  INBOX_ENTITY_LINK_FIELDS,
  searchInboxEntityLinks,
  type InboxEntityLinkKey,
} from '../../lib/inboxEntityLinks'

interface InboxEntityLinkPopoverProps {
  defaultEntityKey?: InboxEntityLinkKey
  title?: string
  onSelect: (entityKey: InboxEntityLinkKey, entityId: string, label: string) => void
  onClose: () => void
  className?: string
}

/** Search + pick a CRM record to link (customer, lead, property, job, etc.). */
export function InboxEntityLinkPopover({
  defaultEntityKey = 'customer_id',
  title = 'Link to record',
  onSelect,
  onClose,
  className = 'absolute right-0 top-12 z-20',
}: InboxEntityLinkPopoverProps) {
  const [entityKey, setEntityKey] = useState<InboxEntityLinkKey>(defaultEntityKey)
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<Array<{ id: string; label: string }>>([])
  const [searching, setSearching] = useState(false)

  async function runSearch() {
    if (!searchTerm.trim()) {
      setResults([])
      return
    }
    setSearching(true)
    try {
      setResults(await searchInboxEntityLinks(entityKey, searchTerm))
    } catch {
      setResults([])
    } finally {
      setSearching(false)
    }
  }

  return (
    <div className={`w-80 bg-white rounded-xl shadow-xl border border-gray-200 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <LinkIcon className="w-4 h-4 text-gray-500" />
          {title}
        </h4>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <XIcon className="w-4 h-4" />
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-2">
        Link to a CRM record. Use Assign staff for who is handling this item.
      </p>
      <select
        value={entityKey}
        onChange={(e) => {
          setEntityKey(e.target.value as InboxEntityLinkKey)
          setResults([])
          setSearchTerm('')
        }}
        className="w-full mb-2 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      >
        {INBOX_ENTITY_LINK_FIELDS.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>
      <div className="flex gap-1.5 mb-2">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          placeholder={`Search ${INBOX_ENTITY_LINK_FIELDS.find((f) => f.key === entityKey)?.label || 'records'}...`}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
        <button type="button" onClick={runSearch} className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
          <SearchIcon className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {searching ? (
          <div className="text-center py-3">
            <Loader2Icon className="w-4 h-4 animate-spin mx-auto text-gray-400" />
          </div>
        ) : results.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-2">Search the full tenant list</p>
        ) : (
          results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onSelect(entityKey, r.id, r.label)}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-blue-50 rounded-lg truncate"
            >
              {r.label}
            </button>
          ))
        )}
      </div>
    </div>
  )
}
